import { useMemo, useState } from 'react';
import { Modal } from '@/shared/ui/Modal/Modal';
import {
  useAddClubAccountMutation,
  useChangeClubAccountsPasswordMutation,
  useGetClubDetailsQuery,
  useRemoveClubAccountMutation,
  useUnlinkClubHardwareMutation,
} from '@/entities/Club';
import { CLUB_ROLE_LABELS, type ClubRole } from '@/entities/Club/model/clubRoles';
import { Select } from '@/shared/ui/Select/Select';
import { InputPassword } from '@/shared/ui/InputPassword/InputPassword';
import { Button } from '@/shared/ui/Button/Button';
import { Input } from '@/shared/ui/Input/Input';
import { IconButton } from '@/shared/ui/IconButton/IconButton';
import CloseIcon from '@/shared/assets/icons/close.svg';
import s from './ClubAccountsModal.module.scss';
import { getClubDisplayName, isActiveAccount } from '@/shared/lib/utils/accountPresentation';

interface Props {
  clubId: number;
  clubName: string;
  open: boolean;
  onClose: () => void;
  readOnly?: boolean;
}

export const ClubAccountsModal = ({ clubId, clubName, open, onClose, readOnly = false }: Props) => {
  const { data, isFetching, refetch } = useGetClubDetailsQuery(clubId, { skip: !open });
  const [addAccount, { isLoading: isAdding }] = useAddClubAccountMutation();
  const [removeAccount, { isLoading: isRemoving }] = useRemoveClubAccountMutation();
  const [changePassword, { isLoading: isChanging }] = useChangeClubAccountsPasswordMutation();
  const [unlinkHardware, { isLoading: isUnlinking }] = useUnlinkClubHardwareMutation();

  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');

  const [role, setRole] = useState<'admin' | 'player' | 'observer' | 'developer' | 'viewer'>('admin');
  const [countInput, setCountInput] = useState('1');

  const isDeleted = data?.accountStatus === 'deleted';
  const isReadOnly = readOnly || !isActiveAccount(data?.accountStatus);
  const accounts = isDeleted ? (data?.accounts ?? []) : (data?.accounts ?? []).filter((a) => a.isActive);
  const roleLabel = (r: ClubRole | null) => (r ? (CLUB_ROLE_LABELS[r] ?? r) : '—');
  const displayName = isDeleted
    ? `Удалённый клуб #${clubId}`
    : getClubDisplayName(data ?? { id: clubId, name: clubName });

  const isPasswordValid = (value: string): boolean => {
    if (!value) return false;
    const disallowed = /[^A-Za-z~!?@#$%^&*_\-+()\[\]{}><\/\\|"'\.,:\d]+/g;
    if (disallowed.test(value)) return false;
    if (value.length < 8 || value.length > 64) return false;
    if (!/^(?=.*[a-z])(?=.*[A-Z]).+$/.test(value)) return false;
    if (!/^(?=.*\d).+$/.test(value)) return false;
    if (!/(?=.*[~!?@#$%^&*_\-+()\[\]{}><\/\\|"'\.,:])/.test(value)) return false;
    return true;
  };

  const canChangePassword = useMemo(() => isPasswordValid(pwd) && pwd === pwd2, [pwd, pwd2]);

  const pwdError = useMemo(() => (pwd.length > 0 && !isPasswordValid(pwd) ? ' ' : ''), [pwd]);
  const pwd2Error = useMemo(() => (pwd2.length > 0 && pwd !== pwd2 ? ' ' : ''), [pwd2, pwd]);

  const maskHwid = (hw?: string | null) => {
    if (!hw) return '—';
    if (hw.length <= 8) return hw;
    return `${hw.slice(0, 4)}...${hw.slice(-4)}`;
  };

  const countValue = useMemo(() => {
    const numeric = Number(countInput);
    if (!Number.isFinite(numeric) || numeric < 1) {
      return 1;
    }
    return Math.min(10, Math.floor(numeric));
  }, [countInput]);

  const onAdd = async () => {
    if (isReadOnly) return;
    await addAccount({ id: clubId, role, count: countValue }).unwrap();
    await refetch();
  };
  const onRemove = async () => {
    if (isReadOnly) return;
    await removeAccount({ id: clubId, role, count: countValue }).unwrap();
    await refetch();
  };

  const [confirmAcc, setConfirmAcc] = useState<{ id: number; login: string } | null>(null);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={`Аккаунты клуба: ${displayName}`}
        titleSize="h2"
        contentClassName={`${s.wide} ${confirmAcc ? s.dim : ''}`}
      >
        <div className={s.root}>
          <div className={`${s.list} ${isFetching ? s.loading : ''}`}>
            {accounts.map((acc) => (
              <div key={acc.id} className={s.item}>
                <div className={s.accountLogin}>{isDeleted ? `Удалённый аккаунт #${acc.id}` : acc.login}</div>
                <div className={s.accountRole}>{roleLabel(acc.role)}</div>
                <div className={s.hardwareActions}>
                  <span className={s.hardwareId} title={acc.hardwareId ?? ''}>
                    {maskHwid(acc.hardwareId)}
                  </span>
                  {acc.hardwareId && !isReadOnly && !isDeleted && (
                    <span title="Отвязать HWID">
                      <IconButton
                        size="m"
                        color="red"
                        disabled={isUnlinking}
                        onClick={() => setConfirmAcc({ id: acc.id, login: acc.login ?? '—' })}
                      >
                        <CloseIcon />
                      </IconButton>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {!isReadOnly && (
            <div className={`${s.footer} ${confirmAcc ? s.dim : ''}`}>
              <div className={s.row1}>
                <InputPassword
                  className={s.inputCompact}
                  label=""
                  error={pwdError}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder={'Пароль'}
                />
                <InputPassword
                  className={s.inputCompact}
                  label=""
                  error={pwd2Error}
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  placeholder={'Ещё раз'}
                />
                <Button
                  className={s.rowButton}
                  disabled={!canChangePassword || isChanging}
                  onClick={async () => {
                    if (isReadOnly) return;
                    await changePassword({ id: clubId, password: pwd }).unwrap();
                    setPwd('');
                    setPwd2('');
                    await refetch();
                  }}
                >
                  Сменить
                </Button>
              </div>

              <div className={s.row2}>
                <Select
                  value={role}
                  onChange={setRole as any}
                  options={[
                    { label: 'Администратор', value: 'admin' },
                    { label: 'Игрок', value: 'player' },
                    { label: 'Транслятор', value: 'observer' },
                    { label: 'Наблюдатель', value: 'viewer' },
                    { label: 'Разработчик', value: 'developer' },
                  ]}
                  className={s.select}
                />
                <Input
                  className={s.input}
                  value={countInput}
                  error={''}
                  onChange={(v) => {
                    const next = typeof v === 'string' ? v : v.target.value;
                    if (next === '') {
                      setCountInput('');
                      return;
                    }
                    if (/^\d*$/.test(next)) {
                      const clamped = Math.min(10, Math.max(1, Number(next.slice(0, 2))));
                      setCountInput(String(clamped));
                    }
                  }}
                  onBlur={() => {
                    if (countInput === '' || Number(countInput) < 1) {
                      setCountInput('1');
                    } else {
                      setCountInput(String(Math.min(10, Math.max(1, Number(countInput)))));
                    }
                  }}
                  placeholder={'1-10'}
                  type="text"
                  inputMode="numeric"
                  baseAddon={false}
                />
                <Button size={'m'} className={s.rowButton} disabled={isAdding} onClick={onAdd}>
                  Добавить
                </Button>
                <Button size={'m'} className={s.rowButton} variant={'outline'} disabled={isRemoving} onClick={onRemove}>
                  Удалить
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
      {confirmAcc && (
        <Modal open={true} onClose={() => setConfirmAcc(null)} title={'Отвязка Hardware ID'} titleSize={'h1'}>
          <div className={s.confirmText}>
            Вы действительно хотите очистить Hardware ID для аккаунта {confirmAcc.login}?
          </div>
          <div className={s.confirmActions}>
            <Button
              color={'red'}
              disabled={isUnlinking}
              onClick={async () => {
                if (isReadOnly) return;
                await unlinkHardware([confirmAcc.id]).unwrap();
                setConfirmAcc(null);
                await refetch();
              }}
            >
              Отвязать
            </Button>
            <Button variant={'outline'} onClick={() => setConfirmAcc(null)}>
              Отменить
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};
